import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid } from '@mui/material';
import axios, { AxiosError } from 'axios';
import { FastField, Form, Formik, FormikHelpers } from 'formik';
import qs                                         from 'qs';
import React, { FC, useRef, useState }            from 'react';
import { Link, useNavigate }                       from 'react-router-dom';
import styled                                     from 'styled-components';
import PhoneInput                                 from 'react-phone-input-2';
import { ReactComponent as ImageGroup }           from '../../../images/family-group.svg';
import { LinkLikeText }                           from '../../shared/components/documents/DocumentPicker';
import { LogoBlue }                               from '../../shared/components/Logo';
import { TermsModal }                             from '../../shared/components/modals/Terms/TermsModal';
import { MuiButton }                              from '../../shared/components/MuiButton';
import { EAuthRoutes }                            from '../../shared/constants/constants';
import { CheckboxField }                          from '../../shared/formFields/CheckboxField';
import { PasswordInputField }                     from '../../shared/formFields/PasswordInputField';
import { TextInputField }                         from '../../shared/formFields/TextInputField';
import { TextBrandColor }                         from '../../shared/styledComponents';
import { getAuthToken, isTokenExpired }           from '../../utils/authHelpers';
import { IErrorResponse }                         from '../shared/interfaces';

import 'react-phone-input-2/lib/style.css';

import {
  AuthField,
  AuthSVGSceneHolder,
  PageHeader,
  PageHeaderText,
  ReverseGridContainer,
  SignInText,
  SignInUpPage,
  SignUpContainer,
  Terms,
}                                                 from '../shared/styles';
import { signUpFormSchema }                       from './validation';

interface ISignUpForm {
  fullName: string;
  email: string;
  phoneNumber?: string;
  password: string;
  confirmPassword: string;
  news: boolean;
}

const CheckboxGrid = styled(Grid)`&& {
  padding-top: 3px;
}`;

export const SignUpHeaderText = styled(PageHeaderText)`&& {
  ${ ({ theme: { breakpoints } }) => breakpoints.up('sm') } {
    width: auto;
  }

  ${ ({ theme: { breakpoints } }) => breakpoints.up('md') } {
    width: 100%;
    padding: 20px 0 0;
  }

  ${ ({ theme: { breakpoints } }) => breakpoints.up('lg') } {
    width: 100%;
    padding: 20px 0 0;
  }
}`;

const SignUpSvgContainer = styled(AuthSVGSceneHolder)`
  ${ ({ theme: { breakpoints } }) => breakpoints.up('md') } {
    margin: -25px 75px 0 0 !important;
  }
`;

const CheckBoxWrapper = styled.div`
  margin-left: -11px;
`;

interface ISignUpFormContentProps {
  handleChange: any;
  handleBlur: any;
  handleSubmit: any;
  isSubmitting: boolean;
  isValid: boolean;
  values: ISignUpForm;
  errors: any;
  touched: any;
  phone: string;
  setPhone: (value: string) => void;
  readOnly: boolean;
  setReadOnly: (value: boolean) => void;
  setTermsModalOpened: (value: boolean) => void;
  manualDisabled: boolean;
  setManualDisabled: (value: boolean) => void;
}

const SignUpFormContent: FC<ISignUpFormContentProps> = ({
  handleChange,
  handleBlur,
  handleSubmit,
  isSubmitting,
  isValid,
  values,
  errors,
  touched,
  phone,
  setPhone,
  readOnly,
  setReadOnly,
  setTermsModalOpened,
  manualDisabled,
  setManualDisabled,
}) => (
  <Form onSubmit={handleSubmit}>
    <Grid
      container
      spacing={3}
    >
      <Grid
        item
        xs={12}
      >
        <AuthField
          label="Full Name"
          name={'fullName'}
          inputProps={{ maxLength: 100 }}
          component={TextInputField}
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </Grid>

      <Grid
        item
        xs={12}
      >
        <AuthField
          label="Email"
          name={'email'}
          readOnly={readOnly}
          placeholder="you@example.com"
          inputProps={{ maxLength: 100 }}
          component={TextInputField}
          onFocus={()=>setReadOnly(true)}
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </Grid>

      <Grid
        item
        xs={12}
      >
        <FastField
          name="phoneNumber"
        >
          {({ field, form }: any) => (
            <StyledPhoneDiv
              className={form.touched.phoneNumber && form.errors.phoneNumber ? 'error' : ''}
            >
              <label
                htmlFor="phoneNumber"
              >
                  Phone Number
              </label>
              <PhoneInput
                {...field}
                country={'us'}
                inputStyle={{ width: '100%' }}
                specialLabel="Phone Number"
                placeholder="Enter phone number"
                inputProps={{
                  autoComplete : 'tel',
                  id           : 'phoneNumber',
                  name         : 'phoneNumber',
                }}
                onChange={(value) => {
                  setPhone(value || '');
                  form.setFieldValue(field.name, value || '');
                }}
              />
              {form.touched.phoneNumber && form.errors.phoneNumber && (
                <p>
                  {form.errors.phoneNumber}
                </p>
              )}
            </StyledPhoneDiv>
          )}
        </FastField>
      </Grid>

      <Grid
        item
        xs={12}
      >
        <AuthField
          label="Password"
          name={'password'}
          placeholder="At least 8 characters"
          inputProps={{ maxLength: 50 }}
          component={PasswordInputField}
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </Grid>

      <Grid
        item
        xs={12}
      >
        <AuthField
          label="Confirm Password"
          name={'confirmPassword'}
          placeholder="At least 8 characters"
          inputProps={{ maxLength: 50 }}
          component={PasswordInputField}
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </Grid>

      <CheckboxGrid
        item
        xs={12}
      >
        <CheckBoxWrapper>
          <FastField
            label="It's ok to send me emails about Family Core"
            name="news"
            component={CheckboxField}
            onBlur={handleBlur}
            onChange={handleChange}
          />
        </CheckBoxWrapper>
      </CheckboxGrid>
    </Grid>

    <Terms align="left">
        By creating an account you agree to
      <LinkLikeText
        href={'#'}
        className={'ml-2'}
        onClick={(e) => {
          e.preventDefault();
          setTermsModalOpened(true);
        }}
      >
          Terms & Conditions
      </LinkLikeText>.
    </Terms>

    <MuiButton
      fullWidth
      type="submit"
      disabled={isSubmitting || (manualDisabled && !isValid)}
      margin="10px 0 0"
      onClick={() => {
        if (values.email && values.password && values.confirmPassword && values.fullName && values.phoneNumber && isValid && !isSubmitting) {
          setManualDisabled(false);
          return;
        }
        setTimeout(()=>{setManualDisabled(true);},90);
      }}
    >
        Sign Up
    </MuiButton>

    <Divider />

    <SignInText>
        Already have an account? <Link to={EAuthRoutes.SignIn}>Sign In</Link>
    </SignInText>
  </Form>
);

const StyledPhoneDiv = styled.div`
    margin-top: 5px;
    
    label{
        font-weight: 500;
        margin-bottom: 12px;
        display: block;
        font-size: 13px;
    }
    
    &.error label{
        color: #e42e2e;
    }

    &.error .flag-dropdown {
        border-top: 2px solid rgb(228, 46, 46);
        border-bottom: 2px solid rgb(228, 46, 46);
        border-left: 2px solid rgb(228, 46, 46);
        border-right: none;
    }
    .flag-dropdown {
        border-top: 1px solid #cccccc3b;
        border-bottom: 1px solid #cccccc3b;
        border-left: 1px solid #cccccc3b;
        border-right: none;
        background: none;
    }
    .form-control {
        border-radius: 6px;
        height: 40px;
        border: solid 2px transparent;
        color: currentColor;
        background: rgb(231, 239, 255);
    }

    .form-control:not(.error):focus {
        border: solid 2px #547cd3;
        outline: 0;
        box-shadow: 0 0 2px #547cd3;
        background-color: #fafafa;
    }
    .form-control:not(.error):focus ~ .flag-dropdown,
    .form-control:not(.error):focus + .flag-dropdown,
    .form-control:not(.error):focus ~ .react-tel-input .flag-dropdown {
        border-top: 2px solid #547cd3;
        border-bottom: 2px solid #547cd3;
        border-left: 2px solid #547cd3;
        border-right: none;
        box-shadow: 0 0 2px #547cd3;
    }
    
    &.error .form-control {
          border: 2px solid #e42e2e;
          border-radius: 6px;
    }
    &.error p{
        color: #f44336;
        font-size: 0.6666666666666666rem;
    }
`;

export const SignUp: FC = () => {
  const history = useNavigate();
  const [ readOnly, setReadOnly ]                             = useState(true);
  const [ termsModalOpened, setTermsModalOpened ]             = useState(false);
  const [ switchAccountModalOpen, setSwitchAccountModalOpen ] = useState(false);
  const [phone, setPhone]           = useState('');
  const [manualDisabled, setManualDisabled] = useState(false);

  const pendingSubmit = useRef<{ values: ISignUpForm; formikActions: FormikHelpers<ISignUpForm> } | null>(null);

  const initialValues: ISignUpForm = {
    email           : '',
    phoneNumber     : '',
    password        : '',
    fullName        : '',
    confirmPassword : '',
    news            : true,
  };

  const performSignUp = (values: ISignUpForm, formikActions: FormikHelpers<ISignUpForm>) => {
    const [ firstName, lastName ] = values.fullName.split(/\s+/);

    const signUpRequest = {
      email           : values.email,
      phoneNumber     : `+${phone}`,
      password        : values.password,
      confirmPassword : values.confirmPassword,
      news            : values.news,
      firstName,
      lastName,
    };

    axios.post('/api/v1/users', signUpRequest)
      .then(() => {
        history({
          pathname : EAuthRoutes.EmailConfirmation,
          search   : qs.stringify({ email: values.email, familyName: lastName }),
        });
        formikActions.setSubmitting(false);
      })
      .catch((err: AxiosError<IErrorResponse>) => {
        const errorDetails = err?.response?.data?.ErrorDetails;
        if (errorDetails) {
          const { Key, Message } = errorDetails;
          const key = [ 'lastName', 'firstName' ]
            .find(item => item === Key) ? 'fullName' : Key;

          formikActions.setErrors({ [key]: Message });
          formikActions.setSubmitting(false);
        }
      });
  };

  const handleSubmit = (values: ISignUpForm, formikActions: FormikHelpers<ISignUpForm>) => {
    formikActions.setSubmitting(true);

    const authToken  = getAuthToken();
    const isLoggedIn = authToken && !isTokenExpired();

    if (isLoggedIn) {
      pendingSubmit.current = { values, formikActions };
      setSwitchAccountModalOpen(true);
      formikActions.setSubmitting(false);
      return;
    }

    performSignUp(values, formikActions);
  };

  const handleSwitchAccountConfirm = () => {
    setSwitchAccountModalOpen(false);
    if (!pendingSubmit.current) return;
    const { values, formikActions } = pendingSubmit.current;
    pendingSubmit.current = null;
    formikActions.setSubmitting(true);
    performSignUp(values, formikActions);
  };

  const handleSwitchAccountCancel = () => {
    setSwitchAccountModalOpen(false);
    pendingSubmit.current = null;
    history(EAuthRoutes.SelectFamilyComponent);
  };

  return (
    <SignInUpPage>
      <SignUpContainer maxWidth="md">
        <PageHeader mb="32px">
          <LogoBlue margin="0 0 50px" />

          <SignUpHeaderText centeredXs>
            Let’s Get <TextBrandColor>You</TextBrandColor> Started
          </SignUpHeaderText>
        </PageHeader>

        <ReverseGridContainer
          container
          spacing={3}
        >
          <Grid
            item
            sm={7}
            sx={{ display: { xs: 'none', md: 'block' } }}
          >
            <SignUpSvgContainer>
              <ImageGroup />
            </SignUpSvgContainer>
          </Grid>

          <Grid
            item
            xs={12}
            sm={7}
            md={5}
          >
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={signUpFormSchema}
              onSubmit={handleSubmit}
            >
              { ({
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                isValid,
                values,
                errors,
                touched,
              }) => (
                <SignUpFormContent
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  handleSubmit={handleSubmit}
                  isSubmitting={isSubmitting}
                  isValid={isValid}
                  values={values}
                  errors={errors}
                  touched={touched}
                  phone={phone}
                  setPhone={setPhone}
                  readOnly={readOnly}
                  setReadOnly={setReadOnly}
                  setTermsModalOpened={setTermsModalOpened}
                  manualDisabled={manualDisabled}
                  setManualDisabled={setManualDisabled}
                />
              ) }
            </Formik>
          </Grid>
        </ReverseGridContainer>
      </SignUpContainer>

      <TermsModal
        open={termsModalOpened}
        onClose={() => setTermsModalOpened(false)}
      />

      <Dialog open={switchAccountModalOpen} onClose={handleSwitchAccountCancel}>
        <DialogTitle>Switch Account?</DialogTitle>
        <DialogContent>
          You are already signed in. Creating a new account will replace your current session. Do you want to continue?
        </DialogContent>
        <DialogActions>
          <Button color="primary" variant="outlined" onClick={handleSwitchAccountCancel}>
            Cancel
          </Button>
          <Button color="primary" variant="contained" onClick={handleSwitchAccountConfirm}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </SignInUpPage>
  );
};
